import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { doc, getDoc, collection, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import FactionCardRoster from '../components/faction/FactionCardRoster'
import WrestlerAvailabilityList from '../components/roster/WrestlerAvailabilityList'
import { useAuth } from '../auth/AuthContext'
import CreditIcon from '../components/CreditIcon'
import { calculateFactionTotalScore } from '../utils/factionScoring'
import { getFactionStoredWrestlers } from '../utils/factionWrestlers'
import { calculateGmMatchPointsByFaction, normalizeGmMatch } from '../utils/gmMatches'
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync'

function formatDate (timestamp) {
  if (!timestamp) return 'Data sconosciuta'

  let date

  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000)
  } else if (timestamp.toDate) {
    date = timestamp.toDate()
  } else if (timestamp instanceof Date) {
    date = timestamp
  } else {
    date = new Date(timestamp)
  }

  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

function formatRosterScope (roster) {
  if (roster?.onlyNXT) return 'Valgono solo i wrestler di NXT'
  if (roster?.onlyMainRoster)
    return 'Valgono solo i wrestler del Main Roster (Raw e SmackDown)'
  return 'Tutti i brand ammessi'
}

export default function RosterDetails () {
  const { id } = useParams()
  const { currentUser } = useAuth()
  const [roster, setRoster] = useState(null)
  const [factions, setFactions] = useState([])
  const [usersMap, setUsersMap] = useState({})
  const [wrestlers, setWrestlers] = useState({})
  const [gmScoresByFaction, setGmScoresByFaction] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!id) {
      setError('ID roster non valido')
      setLoading(false)
      return
    }

    async function fetchRoster () {
      try {
        const docRef = doc(db, 'rosters', id)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          setRoster({ id: docSnap.id, ...docSnap.data() })
        } else {
          setRoster(null)
        }
      } catch (err) {
        setError('Errore nel recupero del roster')
      }
      setLoading(false)
    }

    fetchRoster()

    const unsubFactions = onSnapshot(collection(db, 'factions'), snap => {
      setFactions(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    })

    const unsubUsers = onSnapshot(collection(db, 'users'), snap => {
      const map = {}
      snap.docs.forEach(d => {
        const userData = d.data()
        map[d.id] = {
          username: userData.username || '-',
          profileImageUrl: userData.profileImageUrl || null
        }
      })
      setUsersMap(map)
    })

    const unsubWrestlers = onSnapshot(collection(db, 'wrestlers'), snap => {
      const map = {}
      snap.docs.forEach(d => {
        const wrestler = d.data()
        map[d.id] = {
          name: wrestler.name || wrestler.nome || '-',
          imageUrl: wrestler.imageUrl || '/placeholder-wrestler.jpg',
          score: wrestler.score || 0,
          active: wrestler.active || false,
          gender:
            wrestler.gender === 'Uomo'
              ? 'M'
              : wrestler.gender === 'Donna'
              ? 'F'
              : 'M',
          brand: wrestler.brand || 'Indy',
          championTypes: wrestler.championTypes
        }
      })
      setWrestlers(map)
    })

    const unsubMatches = onSnapshot(collection(db, 'rosters', id, 'matches'), async snap => {
      const normalizedMatches = snap.docs.map(matchDoc => normalizeGmMatch(matchDoc))
      const { matches } = await autoCloseExpiredGmMatches({
        db,
        rosterId: id,
        matches: normalizedMatches,
        persist: true
      })
      setGmScoresByFaction(calculateGmMatchPointsByFaction(matches))
    })

    return () => {
      unsubFactions()
      unsubUsers()
      unsubWrestlers()
      unsubMatches()
    }
  }, [id])

  const rosterFactions = useMemo(() => {
    if (!roster) return []
    return factions.filter(faction => {
      if (faction.rosterId) {
        return faction.rosterId === roster.id
      }
      return faction.roster === roster.name
    })
  }, [factions, roster])

  const userFaction = useMemo(() => {
    if (!currentUser || !rosterFactions.length) return null
    return rosterFactions.find(faction => faction.ownerUid === currentUser.uid)
  }, [currentUser, rosterFactions])

  const wrestlersArray = useMemo(
    () =>
      Object.keys(wrestlers).map(wrestlerId => ({
        id: wrestlerId,
        name: wrestlers[wrestlerId].name,
        imageUrl: wrestlers[wrestlerId].imageUrl,
        score: wrestlers[wrestlerId].score,
        active: wrestlers[wrestlerId].active,
        gender: wrestlers[wrestlerId].gender || 'M',
        brand: wrestlers[wrestlerId].brand || 'Indy',
        championTypes: wrestlers[wrestlerId].championTypes
      })),
    [wrestlers]
  )

  const [sortConfig, setSortConfig] = useState({
    key: 'totalScore',
    direction: 'desc'
  })

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction:
        prev.key === key ? (prev.direction === 'desc' ? 'asc' : 'desc') : 'desc'
    }))
  }

  const sortedFactions = useMemo(() => {
    const nextFactions = [...rosterFactions]
    const wrestlerScoresById = {}
    Object.keys(wrestlers).forEach(wrestlerId => {
      wrestlerScoresById[wrestlerId] = wrestlers[wrestlerId]?.score || 0
    })

    const getTotalScore = faction =>
      roster?.isGM
        ? (gmScoresByFaction[faction.id] || 0)
        : calculateFactionTotalScore({
            faction,
            roster,
            wrestlerScoresById
          })

    nextFactions.sort((a, b) => {
      const totalScoreA = getTotalScore(a)
      const totalScoreB = getTotalScore(b)

      const creditsA = a.creditsLeft ?? 0
      const creditsB = b.creditsLeft ?? 0
      const nameA = a.name || ''
      const nameB = b.name || ''

      switch (sortConfig.key) {
        case 'name':
          return sortConfig.direction === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA)
        case 'totalScore':
          return sortConfig.direction === 'asc'
            ? totalScoreA - totalScoreB
            : totalScoreB - totalScoreA
        case 'creditsLeft':
          return sortConfig.direction === 'asc'
            ? creditsA - creditsB
            : creditsB - creditsA
        default:
          return 0
      }
    })

    return nextFactions
  }, [gmScoresByFaction, roster, rosterFactions, sortConfig, wrestlers])

  const getGradient = gameType => {
    switch (gameType) {
      case 'Indipendente':
        return 'linear-gradient(135deg, rgba(146, 201, 252, 0.22) 0%, rgba(38, 56, 72, 0.96) 42%, rgba(18, 24, 31, 0.98) 100%)'
      case 'Competitivo':
        return 'linear-gradient(135deg, rgba(216, 185, 130, 0.22) 0%, rgba(86, 62, 23, 0.94) 38%, rgba(18, 24, 31, 0.98) 100%)'
      case 'Road To':
      case 'Road to WrestleMania':
        return 'linear-gradient(135deg, rgba(222, 58, 71, 0.2) 0%, rgba(110, 23, 30, 0.95) 40%, rgba(18, 24, 31, 0.98) 100%)'
      default:
        return 'linear-gradient(135deg, rgba(146, 201, 252, 0.18) 0%, rgba(38, 56, 72, 0.95) 40%, rgba(18, 24, 31, 0.98) 100%)'
    }
  }

  const rosterTone =
    roster?.gameType === 'Competitivo'
      ? 'secondary'
      : roster?.gameType === 'Road To'
      ? 'accent'
      : 'primary'

  if (loading) return <LoadingSpinner fullPage={true} />
  if (error)
    return <div className='container py-5 text-center text-danger'>{error}</div>
  if (!roster)
    return (
      <div className='container py-5 text-center text-light'>
        Roster non trovato
      </div>
    )

  return (
    <div className='container py-4'>
      <div
        className='card border-0 rounded-4 overflow-hidden shadow-sm position-relative'
        style={{
          background: roster.imageUrl
            ? `url(${roster.imageUrl}) center/cover no-repeat`
            : 'none',
          backgroundColor: roster.imageUrl ? 'transparent' : 'var(--bs-dark)'
        }}
      >
        {/* Overlay con gradiente per effetto visivo e leggibilità */}
        <div
          className='position-absolute top-0 start-0 w-100 h-100'
          style={{
            background: getGradient(roster.gameType),
            opacity: 0.85,
            pointerEvents: 'none'
          }}
        ></div>

        <div className='card-body p-3 p-md-4 p-lg-5 position-relative'>
          {/* Prima riga: titolo e primi 4 dati (2 colonne su schermi medi e grandi) */}
          <div className='row g-3 g-md-4 align-items-start mb-3 mb-md-4'>
            <div className='col-12 col-xl-5'>
              <div className='roster-details-hero-copy'>
                {roster.imageUrl && (
                  <div className='d-flex justify-content-center mb-3'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                      }}
                    >
                      <img
                        src={roster.imageUrl}
                        alt={roster.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          borderRadius: '16px' // Angoli smussati
                        }}
                        onError={event => {
                          event.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                  </div>
                )}
                <h1 className='text-center brand-title mb-2 text-white roster-details-hero__title'>
                  {roster.name}
                </h1>
              </div>
            </div>
            <div className='col-12 col-xl-7'>
              {/* Griglia Bootstrap a 2 colonne per i 4 dati principali */}
              <div className='row g-3'>
                <div className='col-6'>
                  <div className='roster-details-summary__item roster-details-summary__item--primary'>
                    <span className='brand-stat__label d-block small text-white-50'>
                      Partecipanti
                    </span>
                    <strong className='text-white'>
                      {rosterFactions.length}
                    </strong>
                  </div>
                </div>
                <div className='col-6'>
                  <div className='roster-details-summary__item roster-details-summary__item--primary'>
                    <span className='brand-stat__label d-block small text-white-50'>
                      Wrestler per fazione
                    </span>
                    <strong className='text-white'>
                      {roster.maxWrestlers || 0}
                    </strong>
                  </div>
                </div>
                <div className='col-6'>
                  <div className='roster-details-summary__item roster-details-summary__item--primary'>
                    <span className='brand-stat__label d-block small text-white-50'>
                      Crediti
                    </span>
                    <strong className='text-white'>
                      <CreditIcon className='text-light me-1' size={20} />
                      {roster.maxCredits}
                    </strong>
                  </div>
                </div>
                <div className='col-6'>
                  <div className='roster-details-summary__item roster-details-summary__item--primary'>
                    <span className='brand-stat__label d-block small text-white-50'>
                      Inizio
                    </span>
                    <strong className='text-white'>
                      {formatDate(roster.dataInizioPunteggio)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Seconda riga: altri dati (minimi, backstage, ecc.) sempre in 2 colonne */}
          <div className='row g-3 mb-4'>
            <div className='col-6'>
              <div className='roster-details-summary__item roster-details-summary__item--secondary'>
                <span className='brand-stat__label d-block small text-white-50'>
                  Min. uomini e donne
                </span>
                <strong className='text-secondary'>
                  {' '}
                  <span style={{ color: 'rgb(75, 138, 255)' }}>
                    {roster.minMaleWrestlers || 0}
                  </span>{' '}
                  |{' '}
                  <span style={{ color: 'rgb(255, 75, 75)' }}>
                    {roster.minFemaleWrestlers || 0}
                  </span>
                </strong>
              </div>
            </div>
            <div className='col-6'>
              <div className='roster-details-summary__item roster-details-summary__item--secondary'>
                {(() => {
                  const ringCount =
                    roster.maxWrestlers -
                    (roster.hasMainEventer ? 1 : 0) -
                    (roster.hasBackstage ? roster.backstageCount || 0 : 0)
                  const backCount = roster.hasBackstage
                    ? roster.backstageCount || 0
                    : 0
                  const mainCount = roster.hasMainEventer ? 1 : 0
                  const total = roster.maxWrestlers
                  const ringPercent = (ringCount / total) * 100
                  const backPercent = (backCount / total) * 100
                  const mainPercent = (mainCount / total) * 100
                  return (
                    <>
                      <div
                        className='progress mb-2'
                        style={{
                          height: '8px',
                          backgroundColor: 'rgba(169, 204, 44, 0.96)'
                        }}
                      >
                        {mainCount > 0 && (
                          <div
                            className='progress-bar bg-brand-secondary-dark'
                            style={{ width: `${mainPercent}%` }}
                          ></div>
                        )}
                        {ringCount > 0 && (
                          <div
                            className='progress-bar bg-brand-primary-ultralight'
                            style={{ width: `${ringPercent}%` }}
                          ></div>
                        )}
                        {backCount > 0 && (
                          <div
                            className='progress-bar bg-secondary'
                            style={{ width: `${backPercent}%` }}
                          ></div>
                        )}
                      </div>
                      <div className='d-flex flex-wrap justify-content-between small text-white-50'>
                        {mainCount > 0 && (
                          <span>
                            <i className='bi bi-star-fill text-warning me-1'></i>
                            Main {mainCount}
                          </span>
                        )}
                        <span>
                          <i className='bi bi-square-fill text-brand-primary-ultralight me-1'></i>
                          Ring {ringCount}
                        </span>
                        {backCount > 0 && (
                          <span>
                            <i className='bi bi-x-square text-secondary me-1'></i>
                            Back {backCount}
                          </span>
                        )}
                      </div>
                    </>
                  )
                })()}
              </div>
            </div>
            {/* GM Mode e Restrizione - visualizzazione condizionale con larghezza dinamica */}
            {(roster.isGM || roster.onlyNXT || roster.onlyMainRoster) && (
              <div className='row g-2'>
                {roster.isGM && (
                  <div
                    className={
                      roster.onlyNXT || roster.onlyMainRoster
                        ? 'col-6'
                        : 'col-12'
                    }
                  >
                    <div
                      className='roster-details-summary__item roster-details-summary__item--secondary'
                      style={{ backgroundColor: 'var(--brand-purple)' }}
                    >
                      <span className='brand-stat__label d-block small text-white-50'>
                        GM Mode
                      </span>
                      <strong className='text-white'>
                        {roster.isGM ? 'Attiva' : 'Disattiva'}
                      </strong>
                    </div>
                  </div>
                )}
                {(roster.onlyNXT || roster.onlyMainRoster) && (
                  <div className={roster.isGM ? 'col-6' : 'col-12'}>
                    <div className='roster-details-summary__item roster-details-summary__item--secondary'>
                      <span className='brand-stat__label d-block small text-white-50'>
                        Restrizione
                      </span>
                      <strong className='text-white'>
                        {roster.onlyNXT ? 'Solo NXT' : 'Solo Main Roster'}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {userFaction && (
            <div className='alert border-0 rounded-4 mb-4 bg-brand-secondary-dark text-dark'>
              <div className='d-flex align-items-start gap-3'>
                <div className='flex-shrink-0'>
                  <i className='bi bi-check-circle fs-4' />
                </div>
                <div>
                  <h6 className='mb-1 fw-semibold'>
                    Partecipi a questo roster con la fazione
                  </h6>
                  <span className='fw-bold'>{userFaction.name}</span>
                </div>
              </div>
            </div>
          )}

          <div className='row g-4'>
            {roster.description && (
              <div className='col-12 col-xl-7'>
                <div className='roster-details-panel p-3 rounded-3 bg-white bg-opacity-10 h-100'>
                  <p className='mb-0 text-white roster-details-panel__copy'>
                    {roster.description}
                  </p>
                </div>
              </div>
            )}

            <div className='col-12 col-xl-5'>
              <div className='roster-details-panel p-3 rounded-3 bg-white bg-opacity-10 h-100'>
                <span className='small text-white-50 d-block mb-2'>
                  Regole per le fazioni
                </span>
                <div className='roster-details-rules-list d-flex flex-column gap-2'>
                  <div className='text-white'>
                    <i className='bi bi-dot me-1'></i> Ogni fazione parte con{' '}
                    <strong>{roster.maxCredits} crediti</strong>.
                  </div>
                  <div className='text-white'>
                    <i className='bi bi-dot me-1'></i> Ogni fazione deve avere{' '}
                    <strong>{roster.maxWrestlers} wrestler</strong>.
                  </div>
                  <div className='text-white'>
                    <i className='bi bi-dot me-1'></i>
                    <strong>{formatRosterScope(roster)}</strong>.
                  </div>
                  <div className='text-white'>
                    <i className='bi bi-dot me-1'></i>{' '}
                    {roster.hasMainEventer !== false
                      ? 'La lineup prevede 1 slot main eventer.'
                      : 'La lineup non prevede un main eventer obbligatorio.'}
                  </div>
                  {roster.hasBackstage ? (
                    <div className='text-white'>
                      <i className='bi bi-dot me-1'></i>
                      {roster.backstageCount === 1
                        ? `${roster.backstageCount} wrestler dovrà essere in backstage.`
                        : `${roster.backstageCount} wrestler dovranno essere in backstage.`}
                    </div>
                  ) : (
                    <div className='text-white'>
                      <i className='bi bi-dot me-1'></i> Non è previsto il
                      backstage.
                    </div>
                  )}
                  {roster.isGM && (
                    <div className='text-white'>
                      <i className='bi bi-dot me-1'></i> Roster competitivo in
                      modalità GM
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {userFaction && roster.gameType === 'Competitivo' && (
        <div className='row mt-4'>
          <div className='col-12'>
            <WrestlerAvailabilityList
              wrestlers={wrestlersArray}
              factions={rosterFactions}
              title='Wrestler Svincolati'
              defaultExpanded={false}
              showFilters
              onlyMainRoster={Boolean(roster.onlyMainRoster)}
              onlyNXT={Boolean(roster.onlyNXT)}
            />
          </div>
        </div>
      )}

      <div className='mt-5'>
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-center align-items-md-center mb-4 gap-3'>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            <div className='btn-group' role='group'>
              <button
                className={`btn btn-sm ${
                  sortConfig.key === 'totalScore'
                    ? 'btn-brand-secondary'
                    : 'btn-outline-warning'
                }`}
                onClick={() => handleSort('totalScore')}
                title='Ordina per punteggio totale'
              >
                <i className='bi bi-trophy me-1' />
                Classifica
                {sortConfig.key === 'totalScore' && (
                  <i
                    className={`bi bi-arrow-${
                      sortConfig.direction === 'asc' ? 'up' : 'down'
                    } ms-1`}
                  />
                )}
              </button>

              <button
                className={`btn btn-sm ${
                  sortConfig.key === 'creditsLeft'
                    ? 'btn-brand-money'
                    : 'btn-outline-success'
                }`}
                onClick={() => handleSort('creditsLeft')}
                title='Ordina per crediti disponibili'
              >
                <CreditIcon className='me-1' size={16} />
                Crediti
                {sortConfig.key === 'creditsLeft' && (
                  <i
                    className={`bi bi-arrow-${
                      sortConfig.direction === 'desc' ? 'up' : 'down'
                    } ms-1`}
                  />
                )}
              </button>

              <button
                className={`btn btn-sm ${
                  sortConfig.key === 'name' ? 'btn-light' : 'btn-outline-light'
                }`}
                onClick={() => handleSort('name')}
                title='Ordina per nome'
              >
                <i className='bi bi-sort-alpha-down me-1' />
                Nome
                {sortConfig.key === 'name' && (
                  <i
                    className={`bi bi-arrow-${
                      sortConfig.direction === 'asc' ? 'up' : 'down'
                    } ms-1`}
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        {rosterFactions.length === 0 ? (
          <div
            className='card border-0 rounded-4 text-center py-5 bg-transparent'
            style={{ color: 'white' }}
          >
            <div
              className='d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto'
              style={{
                width: '60px',
                height: '60px',
                background: 'rgba(255, 255, 255, 0.1)'
              }}
            >
              <i className='bi bi-inbox fs-1 text-brand-secondary' />
            </div>
            <h5 className='section-title'>Nessuna fazione trovata</h5>
            <p className='text-brand-muted mb-0'>
              Questo roster non ha ancora fazioni partecipanti.
            </p>
          </div>
        ) : (
          <div className='row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4'>
            {sortedFactions.map(faction => {
              const members = getFactionStoredWrestlers(faction)
                .map(member => {
                  const wrestlerId = member.id
                  const wrestler = wrestlers[wrestlerId] || {}
                  return {
                    id: wrestlerId,
                    name: wrestler.name || wrestler.nome || '-',
                    imageUrl: wrestler.imageUrl || '/placeholder-wrestler.jpg',
                    isInjured: wrestler.isInjured || false,
                    gender: wrestler.gender || 'M',
                    brand: wrestler.brand || 'Indy',
                    championTypes: wrestler.championTypes
                  }
                })
                .slice(0, 8)
              const wrestlerScoresById = {}
              Object.keys(wrestlers).forEach(wrestlerId => {
                wrestlerScoresById[wrestlerId] =
                  wrestlers[wrestlerId]?.score || 0
              })
              const totalScore = calculateFactionTotalScore({
                faction,
                roster,
                wrestlerScoresById
              })
              const displayScore = roster?.isGM
                ? (gmScoresByFaction[faction.id] || 0)
                : totalScore
              const isUserFaction = userFaction && faction.id === userFaction.id

              return (
                <div className='col' key={faction.id}>
                  <FactionCardRoster
                    faction={{
                      ...faction,
                      totalScore: displayScore,
                      maxWrestlers: roster?.maxWrestlers || 8
                    }}
                    ownerName={
                      usersMap[faction.ownerUid]?.username ||
                      faction.owner ||
                      'Sconosciuto'
                    }
                    members={members}
                    highlighted={isUserFaction}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
